import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  Icon,
  InputLabel,
  List,
  ListItemButton,
  ListItemButtonProps,
  MenuItem,
  Select,
  styled,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { Link, LinkProps } from 'react-router-dom-v5-compat'

import IconText from '@/app/component/layout/icon-text'
import FeedbackDialog from '@/app/module/user/component/feedback-dialog'
import { Balance, Organization, SelectOption } from '@/app/types'

import { mobileAccountNavMap, mobileAuthNavMap } from './definitions'
import { isPageActive } from './utils'

type Props = {
  open: boolean
  hasOrg: boolean
  activeOrg?: Organization
  balance?: Balance
  organizations?: SelectOption<number>[]
  email: string
  currentPage: string
  logout: () => void
  onClose: () => void
  onOrgSelect: (org: SelectOption<number>) => void
}

const MobileDrawer: React.FC<Props> = ({
  open,
  hasOrg,
  activeOrg,
  balance,
  organizations,
  email,
  currentPage,
  logout,
  onClose,
  onOrgSelect,
}) => {
  const [feedbackOpen, setFeedbackOpen] = useState(false)

  const handleFeedbackOpen = () => setFeedbackOpen(true)
  const handleFeedbackClose = () => setFeedbackOpen(false)

  return (
    <Box
      sx={{
        display: {
          xs: 'block',
          md: 'none',
        },
      }}
    >
      <Drawer open={open} onClose={onClose}>
        <StyledList>
          <ListItemButton disabled>
            <IconText>
              <Icon>person</Icon>
              <Typography>{email}</Typography>
            </IconText>
          </ListItemButton>

          {hasOrg && organizations && organizations.length > 1 && (
            <Box sx={{ px: 2, py: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Organization</InputLabel>
                <Select
                  value={activeOrg?.orgId || ''}
                  label="Organization"
                  onChange={(e) => {
                    const selectedOrg = organizations.find((org) => org.value === e.target.value)
                    if (selectedOrg) {
                      onOrgSelect(selectedOrg)
                    }
                  }}
                >
                  {organizations.map((org) => (
                    <MenuItem key={org.value} value={org.value}>
                      {org.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {hasOrg && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1 }}>
              <Typography variant="body2">
                Balance: {balance?.balanceHuman ? `$${balance.balanceHuman}` : 'N/A'}
              </Typography>
              <Button component={Link} to="/billing" size="small" variant="outlined">
                Add funds
              </Button>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {mobileAuthNavMap.map((navItem) => (
            <StyledListItemButton
              data-testid="nav-mobile-item"
              id={`nav-${navItem.id}`}
              key={navItem.url}
              component={Link}
              to={navItem.url}
              selected={isPageActive(currentPage, navItem.pageGroup)}
              onClick={onClose}
            >
              <IconText>
                <Icon data-testid="nav-mobile-icon">{navItem.icon}</Icon>
                <Typography data-testid="nav-mobile-title">{navItem.title}</Typography>
              </IconText>
            </StyledListItemButton>
          ))}

          <Divider sx={{ my: 2 }} />

          <AccountMenuSection>
            <List>
              {mobileAccountNavMap
                .filter((item) => hasOrg || item.displayWithoutOrg)
                .map((item) => (
                  <StyledListItemButton
                    data-testid="nav-mobile-item"
                    id={`nav-${item.id}`}
                    key={item.url}
                    component={Link}
                    to={item.url}
                    selected={isPageActive(currentPage, item.pageGroup)}
                    onClick={onClose}
                  >
                    <IconText>
                      <Icon data-testid="nav-mobile-icon">{item.icon}</Icon>
                      <Typography data-testid="nav-mobile-title">{item.title}</Typography>
                    </IconText>
                  </StyledListItemButton>
                ))}

              <Divider />

              <ListItemButton
                data-testid="nav-mobile-item"
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onClick={() => {
                  handleFeedbackOpen()
                  onClose()
                }}
              >
                <IconText>
                  <Icon data-testid="nav-mobile-icon">feedback</Icon>
                  <Typography data-testid="nav-mobile-title">Feedback</Typography>
                </IconText>
              </ListItemButton>

              <Divider />

              <ListItemButton
                data-testid="nav-mobile-item"
                onClick={() => {
                  logout()
                  onClose()
                }}
              >
                <IconText>
                  <Icon data-testid="nav-mobile-icon">exit_to_app</Icon>
                  <Typography data-testid="nav-mobile-title">Log out</Typography>
                </IconText>
              </ListItemButton>
            </List>
          </AccountMenuSection>
        </StyledList>
      </Drawer>
      <FeedbackDialog open={feedbackOpen} onClose={handleFeedbackClose} />
    </Box>
  )
}

const StyledList = styled(List)({
  paddingTop: 64,
  width: 250,
})

const AccountMenuSection = styled('div')({
  padding: 0,
})

const StyledListItemButton = styled(ListItemButton)<ListItemButtonProps & LinkProps>(({ theme }) => ({
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))

export default MobileDrawer
